/**
 * @module index
 * @package @geenius/adapters/memory
 * @description Implements the launch memory DB provider as an in-process
 * `Map`-backed adapter for tests, Storybook fixtures, and local development.
 */

import type {
  AdapterJsonValue,
  AuthIdentity,
  AuthProviderAdapter,
  AuthSessionContract,
  CacheAdapter,
  CacheSetOptions,
  DbAdapter,
  DeployAdapter,
  DeploymentResult,
  DeploymentTarget,
  EventEnvelope,
  EventPublishInput,
  EventSubscribeInput,
  EventSubscription,
  EventsAdapter,
  ListOptions,
  PaymentCheckoutSession,
  PaymentPlanContract,
  PaymentSubscriptionContract,
  PaymentsProviderAdapter,
  QueryCondition,
  QueryFilter,
  QueueAdapter,
  QueueJob,
  QueueJobInput,
  StorageAdapter,
  StorageObject,
  StorageWriteInput,
  WebhookDelivery,
  WebhookEmitInput,
} from "@geenius/adapters";
import {
  AdapterConfigurationError,
  AdapterJsonValueSchema,
  AdapterListOptionsSchema,
  AuthContractError,
  CacheContractError,
  CacheSetOptionsSchema,
  DbError,
  DeployContractError,
  DeploymentTargetSchema,
  EventPublishInputSchema,
  EventSubscribeInputSchema,
  EventsContractError,
  PaymentCheckoutInputSchema,
  PaymentPlanContractSchema,
  PaymentsContractError,
  QueueContractError,
  QueueJobInputSchema,
  StorageContractError,
  StorageWriteInputSchema,
  WebhookDeliverySchema,
  WebhookEmitInputSchema,
} from "@geenius/adapters";

/** In-memory record shape stored by the Memory DB adapter. */
export type MemoryDbRecord = Record<string, unknown> & { id: string };
type MemoryKeyPart = "collection" | "field" | "id";
type ContractErrorFactory = (message: string, cause?: unknown) => Error;
type ContractSchema<T> = {
  parse(value: unknown): T;
};

/**
 * Configuration accepted by {@link createMemoryDbAdapter}.
 *
 * The adapter is intended for tests, Storybook fixtures, and local development.
 * It refuses production-like runtimes when `environment` or `NODE_ENV` is
 * `"production"` unless `allowProduction: true` is supplied explicitly.
 * Process-less browser, Storybook, and edge test runtimes are treated as
 * development-like unless callers pass `environment: "production"`. Use `seed`
 * for deterministic in-memory collections and `idFactory` for stable generated
 * ids in tests.
 */
export interface MemoryDbAdapterOptions {
  allowProduction?: boolean;
  environment?: string;
  idFactory?: () => string;
  seed?: Record<string, readonly MemoryDbRecord[]>;
}

/** Binding object accepted by {@link withMemoryDb} for existing adapters. */
export interface MemoryDbAdapterBinding {
  adapter: DbAdapter;
}

/** Accepted input for creating or binding a Memory DB adapter. */
export type MemoryDbAdapterInput =
  | DbAdapter
  | MemoryDbAdapterBinding
  | MemoryDbAdapterOptions
  | undefined;

/** Error thrown when Memory adapter configuration is invalid. */
export class MemoryBindingError extends AdapterConfigurationError {
  readonly memoryCode = "INVALID_MEMORY_ADAPTER" as const;

  /**
   * Creates a typed Memory adapter binding error.
   *
   * @param message Human-readable validation failure.
   */
  constructor(message: string) {
    super(message, "INVALID_CONFIG");
    Object.defineProperty(this, "name", {
      value: "MemoryBindingError",
      enumerable: false,
      configurable: true,
    });
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const adapterMethods = [
  "create",
  "get",
  "update",
  "delete",
  "list",
  "query",
  "count",
] as const;
const queryOperators = new Set<QueryCondition["operator"]>([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "contains",
]);
const optionKeys: ReadonlySet<keyof MemoryDbAdapterOptions> = new Set([
  "allowProduction",
  "environment",
  "idFactory",
  "seed",
]);

function defaultIdFactory(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `memory_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMemoryRecord(value: unknown): value is MemoryDbRecord {
  return isRecord(value) && typeof value.id === "string";
}

function isMemoryOptions(value: unknown): value is MemoryDbAdapterOptions {
  return (
    isRecord(value) &&
    !("adapter" in value) &&
    Object.keys(value).every((key): key is keyof MemoryDbAdapterOptions =>
      optionKeys.has(key as keyof MemoryDbAdapterOptions),
    )
  );
}

function assertBindingKeyPart(value: string, part: MemoryKeyPart): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new MemoryBindingError(`memory ${part} must be a non-empty string`);
  }

  return value;
}

function assertOperationKeyPart(value: string, part: MemoryKeyPart): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DbError(
      `memory ${part} must be a non-empty string`,
      "QUERY_ERROR",
    );
  }

  return value;
}

function assertWritePayload(
  value: unknown,
  operation: "create" | "update",
): Record<string, unknown> {
  if (!isRecord(value)) {
    throw new DbError(
      `memory ${operation} payload must be a record`,
      "QUERY_ERROR",
    );
  }

  return value;
}

function assertNoDuplicateIds(
  collection: string,
  records: readonly MemoryDbRecord[],
): void {
  const seen = new Set<string>();

  for (const record of records) {
    assertBindingKeyPart(record.id, "id");
    if (seen.has(record.id)) {
      throw new MemoryBindingError(
        `memory seed for ${collection} contains duplicate id ${record.id}`,
      );
    }
    seen.add(record.id);
  }
}

function getRuntimeEnvironment(): string | undefined {
  const runtime = globalThis as {
    process?: {
      env?: {
        NODE_ENV?: string;
      };
    };
  };

  return runtime.process?.env?.NODE_ENV;
}

function isProductionLikeRuntime(environment: string | undefined): boolean {
  return environment === "production";
}

function cloneValue<T>(value: T, createError?: ContractErrorFactory): T {
  try {
    return structuredClone(value);
  } catch (error) {
    if (createError) {
      throw createError("Failed to clone memory adapter payload", error);
    }
    throw new DbError(
      "Failed to clone memory adapter payload",
      "QUERY_ERROR",
      error,
    );
  }
}

function addDaysToIsoTimestamp(timestamp: string, days: number): string {
  const millis = Date.parse(timestamp);
  if (Number.isNaN(millis)) {
    return timestamp;
  }

  return new Date(millis + days * 24 * 60 * 60 * 1000).toISOString();
}

function cloneRecord(record: MemoryDbRecord): MemoryDbRecord {
  return cloneValue(record);
}

function cloneRecords(records: readonly MemoryDbRecord[]): MemoryDbRecord[] {
  return records.map((record) => cloneRecord(record));
}

function assertOptions(options: MemoryDbAdapterOptions): void {
  if (
    options.allowProduction !== undefined &&
    typeof options.allowProduction !== "boolean"
  ) {
    throw new MemoryBindingError("memory allowProduction must be a boolean");
  }

  if (
    options.environment !== undefined &&
    (typeof options.environment !== "string" ||
      options.environment.trim().length === 0)
  ) {
    throw new MemoryBindingError(
      "memory environment must be a non-empty string",
    );
  }

  const environment = options.environment ?? getRuntimeEnvironment();
  if (
    isProductionLikeRuntime(environment) &&
    options.allowProduction !== true
  ) {
    throw new MemoryBindingError(
      "memory adapter is for development and tests; pass allowProduction: true to opt in explicitly",
    );
  }

  if (
    options.idFactory !== undefined &&
    typeof options.idFactory !== "function"
  ) {
    throw new MemoryBindingError("memory idFactory must be a function");
  }

  if (options.seed !== undefined && !isRecord(options.seed)) {
    throw new MemoryBindingError("memory seed must be a collection record map");
  }

  for (const [collection, records] of Object.entries(options.seed ?? {})) {
    assertBindingKeyPart(collection, "collection");
    if (!Array.isArray(records) || !records.every(isMemoryRecord)) {
      throw new MemoryBindingError(
        `memory seed for ${collection} must be an array of records with string ids`,
      );
    }
    assertNoDuplicateIds(collection, records);
  }
}

function assertGeneratedId(id: string): string {
  if (typeof id !== "string" || id.trim().length === 0) {
    throw new DbError(
      "memory idFactory must return a non-empty string",
      "QUERY_ERROR",
    );
  }

  return id;
}

function assertNonNegativeInteger(
  value: number | undefined,
  optionName: string,
): void {
  if (value === undefined) {
    return;
  }

  if (!Number.isInteger(value) || value < 0) {
    throw new DbError(
      `${optionName} must be a non-negative integer`,
      "QUERY_ERROR",
    );
  }
}

function assertListOptions(options: ListOptions | undefined): void {
  if (!options) {
    return;
  }

  assertNonNegativeInteger(options.limit, "list limit");
  assertNonNegativeInteger(options.offset, "list offset");

  if (options.orderBy !== undefined) {
    assertOperationKeyPart(options.orderBy, "field");
  }

  if (
    options.order !== undefined &&
    options.order !== "asc" &&
    options.order !== "desc"
  ) {
    throw new DbError(
      'list order must be either "asc" or "desc"',
      "QUERY_ERROR",
    );
  }
}

function isComparableQueryValue(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

function assertQueryConditionOperand(
  condition: QueryCondition,
  methodName: string,
  index: number,
): void {
  switch (condition.operator) {
    case "eq":
    case "neq":
      return;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      if (!isComparableQueryValue(condition.value)) {
        throw new DbError(
          `${methodName} filter condition ${index} requires a string or number value`,
          "QUERY_ERROR",
        );
      }
      return;
    case "in":
      if (!Array.isArray(condition.value)) {
        throw new DbError(
          `${methodName} filter condition ${index} requires an array value`,
          "QUERY_ERROR",
        );
      }
      return;
    case "contains":
      if (typeof condition.value !== "string") {
        throw new DbError(
          `${methodName} filter condition ${index} requires a string value`,
          "QUERY_ERROR",
        );
      }
      return;
  }
}

function assertQueryFilter(
  filter: QueryFilter,
  methodName: string,
): QueryFilter {
  if (!Array.isArray(filter)) {
    throw new DbError(
      `${methodName} filter must be an array of query conditions`,
      "QUERY_ERROR",
    );
  }

  for (const [index, condition] of filter.entries()) {
    if (
      !isRecord(condition) ||
      typeof condition.field !== "string" ||
      condition.field.trim().length === 0 ||
      !queryOperators.has(condition.operator as QueryCondition["operator"])
    ) {
      throw new DbError(
        `${methodName} filter condition ${index} is invalid`,
        "QUERY_ERROR",
      );
    }
    assertQueryConditionOperand(condition, methodName, index);
  }

  return filter;
}

function compareValues(
  left: unknown,
  right: unknown,
  operator: Extract<QueryCondition["operator"], "gt" | "gte" | "lt" | "lte">,
): boolean {
  if (typeof left === "number" && typeof right === "number") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  if (typeof left === "string" && typeof right === "string") {
    if (operator === "gt") return left > right;
    if (operator === "gte") return left >= right;
    if (operator === "lt") return left < right;
    return left <= right;
  }

  return false;
}

function matchesCondition(
  item: MemoryDbRecord,
  condition: QueryCondition,
): boolean {
  const value = item[condition.field];

  switch (condition.operator) {
    case "eq":
      return value === condition.value;
    case "neq":
      return value !== condition.value;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return compareValues(value, condition.value, condition.operator);
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(value);
    case "contains":
      return (
        typeof value === "string" &&
        typeof condition.value === "string" &&
        value.includes(condition.value)
      );
    default:
      return false;
  }
}

function matchesFilter(item: MemoryDbRecord, filter: QueryFilter): boolean {
  return filter.every((condition: QueryCondition) =>
    matchesCondition(item, condition),
  );
}

function sortRecords(
  records: MemoryDbRecord[],
  options?: ListOptions,
): MemoryDbRecord[] {
  if (!options?.orderBy) {
    return records;
  }

  const direction = options.order === "desc" ? -1 : 1;
  return [...records].sort((left, right) => {
    const leftValue = left[options.orderBy as string];
    const rightValue = right[options.orderBy as string];
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue < rightValue
        ? -direction
        : leftValue > rightValue
          ? direction
          : 0;
    }
    if (typeof leftValue === "string" && typeof rightValue === "string") {
      return leftValue < rightValue
        ? -direction
        : leftValue > rightValue
          ? direction
          : 0;
    }
    return 0;
  });
}

function pageRecords(
  records: MemoryDbRecord[],
  options?: ListOptions,
): MemoryDbRecord[] {
  const offset = options?.offset ?? 0;
  const limit = options?.limit;
  return typeof limit === "number"
    ? records.slice(offset, offset + limit)
    : records.slice(offset);
}

function assertDbAdapter(value: unknown): asserts value is DbAdapter {
  if (!isRecord(value)) {
    throw new MemoryBindingError(
      "Expected a DbAdapter object for memory binding",
    );
  }

  const missing = adapterMethods.filter(
    (method) => typeof value[method] !== "function",
  );
  if (missing.length > 0) {
    throw new MemoryBindingError(
      `Memory DbAdapter is missing methods: ${missing.join(", ")}`,
    );
  }
}

/**
 * Creates an in-process DB adapter for tests, Storybook, and local development.
 *
 * By default this factory rejects production-like runtimes, while allowing
 * process-less browser, Storybook, and edge test runtimes where `process.env`
 * cannot be observed. Pass `environment: "production"` with
 * `allowProduction: true` only when an ephemeral process-local store is
 * intentionally acceptable in a production-like smoke test.
 * For deterministic smoke tests, pass both `seed` and `idFactory` so generated
 * records are stable across runs:
 *
 * ```ts
 * const adapter = createMemoryDbAdapter({
 *   environment: "test",
 *   seed: { users: [{ id: "user_1", name: "Ada" }] },
 *   idFactory: () => "user_2",
 * });
 * ```
 *
 * @param options Optional seed data, id factory, and environment guard settings.
 * @returns A DB adapter backed by an isolated in-memory collection store.
 */
export function createMemoryDbAdapter(
  options: MemoryDbAdapterOptions = {},
): DbAdapter {
  assertOptions(options);

  const idFactory = options.idFactory ?? defaultIdFactory;
  const store = new Map<string, MemoryDbRecord[]>();

  for (const [collection, records] of Object.entries(options.seed ?? {})) {
    store.set(collection, cloneRecords(records));
  }

  const getCollection = (collection: string) =>
    store.get(assertOperationKeyPart(collection, "collection")) ?? [];
  const setCollection = (collection: string, records: MemoryDbRecord[]) => {
    store.set(
      assertOperationKeyPart(collection, "collection"),
      cloneRecords(records),
    );
  };

  return {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      const id = assertGeneratedId(idFactory());
      const payload = assertWritePayload(data, "create");
      const record = cloneRecord({ ...payload, id } as MemoryDbRecord);
      const records = getCollection(collection);
      if (records.some((candidate) => candidate.id === id)) {
        throw new DbError(
          `Duplicate memory record id ${id} in ${collection}`,
          "CONSTRAINT_VIOLATION",
        );
      }
      setCollection(collection, [...records, record]);
      return cloneValue(record) as T & { id: string };
    },
    async get<T>(collection: string, id: string) {
      const recordId = assertOperationKeyPart(id, "id");
      const record = getCollection(collection).find(
        (candidate) => candidate.id === recordId,
      );
      return record ? (cloneValue(record) as T) : null;
    },
    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      const recordId = assertOperationKeyPart(id, "id");
      const records = getCollection(collection);
      const index = records.findIndex((record) => record.id === recordId);
      if (index === -1) {
        return null;
      }
      const payload = assertWritePayload(data, "update");
      const next = cloneRecord({
        ...records[index],
        ...payload,
        id: recordId,
      } as MemoryDbRecord);
      const updated = [...records];
      updated[index] = next;
      setCollection(collection, updated);
      return cloneValue(next) as T;
    },
    async delete(collection: string, id: string) {
      const recordId = assertOperationKeyPart(id, "id");
      const records = getCollection(collection);
      const next = records.filter((record) => record.id !== recordId);
      setCollection(collection, next);
      return next.length !== records.length;
    },
    async list<T>(collection: string, options?: ListOptions) {
      assertListOptions(options);
      return cloneValue(
        pageRecords(
          sortRecords([...getCollection(collection)], options),
          options,
        ),
      ) as T[];
    },
    async query<T>(collection: string, filter: QueryFilter) {
      const checkedFilter = assertQueryFilter(filter, "query");
      return cloneValue(
        getCollection(collection).filter((record) =>
          matchesFilter(record, checkedFilter),
        ),
      ) as T[];
    },
    async count(collection: string, filter?: QueryFilter) {
      const checkedFilter = filter
        ? assertQueryFilter(filter, "count")
        : undefined;
      const records = getCollection(collection);
      return checkedFilter && checkedFilter.length > 0
        ? records.filter((record) => matchesFilter(record, checkedFilter))
            .length
        : records.length;
    },
  };
}

/**
 * Normalizes Memory adapter input into a shared DB adapter instance.
 *
 * @param config Existing DB adapter, adapter binding, Memory options, or undefined.
 * @returns A DB adapter suitable for the shared adapters contract.
 */
export function withMemoryDb(config?: MemoryDbAdapterInput): DbAdapter {
  if (config === undefined) {
    return createMemoryDbAdapter();
  }

  if (isMemoryOptions(config)) {
    return createMemoryDbAdapter(config);
  }

  const adapter =
    isRecord(config) && "adapter" in config ? config.adapter : config;
  assertDbAdapter(adapter);
  return adapter;
}

/** Minimal localStorage-compatible persistence surface used by memory mocks. */
export interface MemoryContractStorage {
  readonly length: number;
  clear(): void;
  getItem(key: string): string | null;
  key(index: number): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

/** Options shared by localStorage-backed memory contract adapters. */
export interface MemoryContractAdapterOptions {
  allowProduction?: boolean;
  environment?: string;
  idFactory?: () => string;
  namespace?: string;
  now?: () => string;
  storage?: MemoryContractStorage;
}

/** Options for the memory auth contract adapter. */
export interface MemoryAuthAdapterOptions extends MemoryContractAdapterOptions {
  sessionTtlSeconds?: number;
}

/** Options for the memory payments contract adapter. */
export interface MemoryPaymentsAdapterOptions
  extends MemoryContractAdapterOptions {
  plans?: readonly PaymentPlanContract[];
}

/** Options accepted by the aggregate memory contract adapter bundle. */
export interface MemoryAdaptersOptions
  extends MemoryAuthAdapterOptions,
    MemoryPaymentsAdapterOptions {}

type EventHandler = (event: EventEnvelope) => void | Promise<void>;
type MemoryStorageObject = StorageObject & { expiresAt?: string };

const defaultMemoryNamespace = "geenius:adapters:memory";

class EphemeralContractStorage implements MemoryContractStorage {
  private readonly values = new Map<string, string>();

  get length(): number {
    return this.values.size;
  }

  clear(): void {
    this.values.clear();
  }

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  key(index: number): string | null {
    return Array.from(this.values.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.values.delete(key);
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }
}

function resolveContractStorage(
  options: MemoryContractAdapterOptions | undefined,
): MemoryContractStorage {
  if (options?.storage) {
    return options.storage;
  }

  const runtime = globalThis as { localStorage?: MemoryContractStorage };
  return runtime.localStorage ?? new EphemeralContractStorage();
}

function assertMemoryContractOptions(
  options: MemoryContractAdapterOptions | undefined,
): void {
  if (
    options?.allowProduction !== undefined &&
    typeof options.allowProduction !== "boolean"
  ) {
    throw new MemoryBindingError("memory allowProduction must be a boolean");
  }

  if (
    options?.environment !== undefined &&
    (typeof options.environment !== "string" ||
      options.environment.trim().length === 0)
  ) {
    throw new MemoryBindingError(
      "memory environment must be a non-empty string",
    );
  }

  const environment = options?.environment ?? getRuntimeEnvironment();
  if (
    isProductionLikeRuntime(environment) &&
    options?.allowProduction !== true
  ) {
    throw new MemoryBindingError(
      "memory adapter is for development and tests; pass allowProduction: true to opt in explicitly",
    );
  }
}

function createContractContext(
  domain: string,
  options?: MemoryContractAdapterOptions,
  createError?: (message: string, cause?: unknown) => Error,
) {
  assertMemoryContractOptions(options);

  const storage = resolveContractStorage(options);
  const namespace = options?.namespace ?? defaultMemoryNamespace;
  const idFactory = options?.idFactory ?? defaultIdFactory;
  const now = options?.now ?? (() => new Date().toISOString());
  const prefix = `${namespace}:${domain}:`;

  const keyFor = (bucket: string) => `${prefix}${bucket}`;
  const toError =
    createError ??
    ((message: string, cause?: unknown) => new Error(message, { cause }));

  const readArray = <T>(bucket: string): T[] => {
    try {
      const raw = storage.getItem(keyFor(bucket));
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed as T[];
    } catch (error) {
      throw toError(
        `memory ${domain} storage read failed for bucket ${bucket}`,
        error,
      );
    }
  };

  const writeArray = <T>(bucket: string, value: readonly T[]): void => {
    try {
      storage.setItem(keyFor(bucket), JSON.stringify(value));
    } catch (error) {
      throw toError(
        `memory ${domain} storage write failed for bucket ${bucket}`,
        error,
      );
    }
  };

  return { idFactory, now, readArray, writeArray };
}

function parseContractValue<T>(
  schema: ContractSchema<T>,
  value: unknown,
  label: string,
  errorFactory: ContractErrorFactory,
): T {
  try {
    return schema.parse(value);
  } catch (error) {
    throw errorFactory(`memory ${label} is invalid`, error);
  }
}

function assertContractKey(key: string, errorFactory: ContractErrorFactory) {
  if (typeof key !== "string" || key.trim().length === 0) {
    throw errorFactory("memory contract key must be a non-empty string");
  }
}

function pageContractRecords<T extends Record<string, unknown>>(
  records: readonly T[],
  options: unknown,
  errorFactory: ContractErrorFactory,
): T[] {
  const pageOptions = validateContractListOptions(options, errorFactory);
  const filtered = pageOptions?.prefix
    ? records.filter((record) =>
        String(record.key ?? record.id ?? "").startsWith(
          pageOptions.prefix ?? "",
        ),
      )
    : [...records];
  let start = 0;
  if (pageOptions?.cursor) {
    const cursorIndex = filtered.findIndex(
      (record) => String(record.key ?? record.id) === pageOptions.cursor,
    );
    if (cursorIndex < 0) {
      throw errorFactory(
        `memory list cursor ${pageOptions.cursor} does not match any record`,
      );
    }
    start = cursorIndex + 1;
  }
  const end = pageOptions?.limit ? start + pageOptions.limit : undefined;
  return cloneValue(filtered.slice(start, end), errorFactory);
}

function validateContractListOptions(
  options: unknown,
  errorFactory: ContractErrorFactory,
): { limit?: number; cursor?: string; prefix?: string } | undefined {
  if (options === undefined) {
    return undefined;
  }
  const parsed = parseContractValue(
    AdapterListOptionsSchema,
    options,
    "list options",
    errorFactory,
  );

  if (parsed.cursor !== undefined && parsed.cursor.trim().length === 0) {
    throw errorFactory("memory list cursor must be a non-empty string");
  }
  if (parsed.prefix !== undefined && parsed.prefix.trim().length === 0) {
    throw errorFactory("memory list prefix must be a non-empty string");
  }

  return parsed;
}

/**
 * Creates a localStorage-backed object storage mock for tests, Storybook, and
 * offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, and environment guard
 * settings used to isolate deterministic local state.
 * @returns A storage adapter implementing object get/put/delete/list and
 * health checks without importing a provider SDK.
 */
export function createMemoryStorageAdapter(
  options?: MemoryContractAdapterOptions,
): StorageAdapter {
  const context = createContractContext(
    "storage",
    options,
    (message, cause) => new StorageContractError(message, cause),
  );
  const currentTimeMs = () => new Date(context.now()).getTime();
  const liveObjects = () =>
    context
      .readArray<MemoryStorageObject>("objects")
      .filter(
        (object) =>
          !object.expiresAt ||
          new Date(object.expiresAt).getTime() > currentTimeMs(),
      );
  const cloneStorageValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new StorageContractError(message, cause),
    );

  return {
    async getObject(key: string) {
      assertContractKey(key, (message) => new StorageContractError(message));
      return liveObjects().find((object) => object.key === key) ?? null;
    },
    async putObject(input: StorageWriteInput) {
      const checkedInput = parseContractValue(
        StorageWriteInputSchema,
        input,
        "storage write input",
        (message, cause) => new StorageContractError(message, cause),
      );
      assertContractKey(
        checkedInput.key,
        (message) => new StorageContractError(message),
      );
      const objects = liveObjects();
      const now = context.now();
      const expiresAt = checkedInput.ttlSeconds
        ? new Date(
            currentTimeMs() + checkedInput.ttlSeconds * 1000,
          ).toISOString()
        : undefined;
      const object: MemoryStorageObject = {
        key: checkedInput.key,
        body: checkedInput.body,
        contentType: checkedInput.contentType,
        etag: context.idFactory(),
        metadata: checkedInput.metadata ?? {},
        size: checkedInput.body.length,
        ...(expiresAt ? { expiresAt } : {}),
        updatedAt: now,
      };
      context.writeArray("objects", [
        ...objects.filter((candidate) => candidate.key !== checkedInput.key),
        object,
      ]);
      return cloneStorageValue(object);
    },
    async deleteObject(key: string) {
      assertContractKey(key, (message) => new StorageContractError(message));
      context.writeArray(
        "objects",
        liveObjects().filter((object) => object.key !== key),
      );
    },
    async listObjects(options) {
      return pageContractRecords(
        liveObjects(),
        options,
        (message) => new StorageContractError(message),
      );
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed key/value cache mock for tests, Storybook, and
 * offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, and environment guard
 * settings used to isolate deterministic local state.
 * @returns A cache adapter implementing get/set/delete/has/clear and health
 * checks without importing Redis, KV, or provider SDKs.
 */
export function createMemoryCacheAdapter(
  options?: MemoryContractAdapterOptions,
): CacheAdapter {
  const context = createContractContext(
    "cache",
    options,
    (message, cause) => new CacheContractError(message, cause),
  );
  const currentTimeMs = () => new Date(context.now()).getTime();
  const cloneCacheValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new CacheContractError(message, cause),
    );

  const liveEntries = () =>
    context
      .readArray<{
        key: string;
        value: unknown;
        expiresAt?: string;
        updatedAt: string;
      }>("entries")
      .filter(
        (entry) =>
          !entry.expiresAt ||
          new Date(entry.expiresAt).getTime() > currentTimeMs(),
      );

  return {
    async get<T extends AdapterJsonValue = AdapterJsonValue>(key: string) {
      assertContractKey(key, (message) => new CacheContractError(message));
      const entry = liveEntries().find((candidate) => candidate.key === key);
      return entry ? (cloneCacheValue(entry.value) as T) : null;
    },
    async set<T extends AdapterJsonValue = AdapterJsonValue>(
      key: string,
      value: T,
      writeOptions?: CacheSetOptions,
    ) {
      assertContractKey(key, (message) => new CacheContractError(message));
      const checkedValue = parseContractValue(
        AdapterJsonValueSchema,
        value,
        "cache value",
        (message, cause) => new CacheContractError(message, cause),
      );
      const checkedOptions = writeOptions
        ? parseContractValue(
            CacheSetOptionsSchema,
            writeOptions,
            "cache set options",
            (message, cause) => new CacheContractError(message, cause),
          )
        : undefined;
      const expiresAt = checkedOptions?.ttlSeconds
        ? new Date(
            currentTimeMs() + checkedOptions.ttlSeconds * 1000,
          ).toISOString()
        : undefined;
      const entries = liveEntries().filter((entry) => entry.key !== key);
      context.writeArray("entries", [
        ...entries,
        {
          key,
          value: cloneCacheValue(checkedValue),
          expiresAt,
          updatedAt: context.now(),
        },
      ]);
    },
    async delete(key: string) {
      assertContractKey(key, (message) => new CacheContractError(message));
      context.writeArray(
        "entries",
        liveEntries().filter((entry) => entry.key !== key),
      );
    },
    async has(key: string) {
      assertContractKey(key, (message) => new CacheContractError(message));
      return liveEntries().some((entry) => entry.key === key);
    },
    async clear(prefix?: string) {
      const entries = prefix
        ? liveEntries().filter((entry) => !entry.key.startsWith(prefix))
        : [];
      context.writeArray("entries", entries);
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed job queue mock for tests, Storybook, and
 * offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, and environment guard
 * settings used to isolate deterministic local state.
 * @returns A queue adapter implementing enqueue, schedule, complete, fail,
 * listJobs, and health checks without importing a queue provider SDK.
 */
export function createMemoryQueueAdapter(
  options?: MemoryContractAdapterOptions,
): QueueAdapter {
  const context = createContractContext(
    "queue",
    options,
    (message, cause) => new QueueContractError(message, cause),
  );
  const jobs = () => context.readArray<QueueJob>("jobs");
  const writeJobs = (value: readonly QueueJob[]) =>
    context.writeArray("jobs", value);
  const cloneQueueValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new QueueContractError(message, cause),
    );

  const upsertJob = (job: QueueJob) => {
    writeJobs([...jobs().filter((candidate) => candidate.id !== job.id), job]);
  };

  const enqueueJob = async (input: QueueJobInput): Promise<QueueJob> => {
    const checkedInput = parseContractValue(
      QueueJobInputSchema,
      input,
      "queue job input",
      (message, cause) => new QueueContractError(message, cause),
    );
    const now = context.now();
    const generatedId = context.idFactory();
    const existing = jobs();
    if (existing.some((candidate) => candidate.id === generatedId)) {
      throw new QueueContractError(
        `memory queue id collision: ${generatedId} already exists`,
      );
    }
    const job: QueueJob = {
      id: generatedId,
      name: checkedInput.name,
      payload: checkedInput.payload ?? null,
      status: checkedInput.runAt ? "scheduled" : "queued",
      runAt: checkedInput.runAt,
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    };
    upsertJob(job);
    return cloneQueueValue(job);
  };

  return {
    enqueue: enqueueJob,
    async schedule(input: QueueJobInput & { runAt: string }) {
      const checkedInput = parseContractValue(
        QueueJobInputSchema,
        input,
        "queue job input",
        (message, cause) => new QueueContractError(message, cause),
      );
      if (
        checkedInput.runAt === undefined ||
        checkedInput.runAt.trim().length === 0
      ) {
        throw new QueueContractError(
          "memory queue schedule runAt must be a non-empty string",
        );
      }
      return enqueueJob({ ...checkedInput, runAt: checkedInput.runAt });
    },
    async complete(jobId: string) {
      assertContractKey(jobId, (message) => new QueueContractError(message));
      const job = jobs().find((candidate) => candidate.id === jobId);
      if (job) {
        upsertJob({ ...job, status: "completed", updatedAt: context.now() });
      }
    },
    async fail(jobId: string) {
      assertContractKey(jobId, (message) => new QueueContractError(message));
      const job = jobs().find((candidate) => candidate.id === jobId);
      if (job) {
        upsertJob({
          ...job,
          attempts: job.attempts + 1,
          status: "failed",
          updatedAt: context.now(),
        });
      }
    },
    async listJobs(options) {
      return pageContractRecords(
        jobs(),
        options,
        (message) => new QueueContractError(message),
      );
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed pub/sub and webhook event mock for tests,
 * Storybook, and offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, and environment guard
 * settings used to isolate deterministic local state.
 * @returns An events adapter implementing publish, subscribe, emitWebhook,
 * listEvents, and health checks without importing an event bus SDK.
 */
export function createMemoryEventsAdapter(
  options?: MemoryContractAdapterOptions,
): EventsAdapter {
  const context = createContractContext(
    "events",
    options,
    (message, cause) => new EventsContractError(message, cause),
  );
  const handlers = new Map<string, Set<EventHandler>>();
  const cloneEventsValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new EventsContractError(message, cause),
    );

  return {
    async publish(input: EventPublishInput) {
      const checkedInput = parseContractValue(
        EventPublishInputSchema,
        input,
        "event publish input",
        (message, cause) => new EventsContractError(message, cause),
      );
      const event: EventEnvelope = {
        id: context.idFactory(),
        topic: checkedInput.topic,
        payload: checkedInput.payload ?? null,
        metadata: checkedInput.metadata ?? {},
        createdAt: context.now(),
      };
      context.writeArray("events", [
        ...context.readArray<EventEnvelope>("events"),
        event,
      ]);
      for (const handler of handlers.get(checkedInput.topic) ?? []) {
        await handler(cloneEventsValue(event));
      }
      return cloneEventsValue(event);
    },
    async subscribe(input: EventSubscribeInput, handler: EventHandler) {
      const checkedInput = parseContractValue(
        EventSubscribeInputSchema,
        input,
        "event subscribe input",
        (message, cause) => new EventsContractError(message, cause),
      );
      const topic = checkedInput.topic;
      const topicHandlers = handlers.get(topic) ?? new Set<EventHandler>();
      topicHandlers.add(handler);
      handlers.set(topic, topicHandlers);
      const subscription: EventSubscription = {
        async unsubscribe() {
          topicHandlers.delete(handler);
        },
      };
      return subscription;
    },
    async emitWebhook(input: WebhookEmitInput) {
      const checkedInput = parseContractValue(
        WebhookEmitInputSchema,
        input,
        "webhook emit input",
        (message, cause) => new EventsContractError(message, cause),
      );
      const delivery = parseContractValue(
        WebhookDeliverySchema,
        {
          id: context.idFactory(),
          url: checkedInput.url,
          eventId: checkedInput.event.id,
          status: "delivered",
          createdAt: context.now(),
        },
        "webhook delivery",
        (message, cause) => new EventsContractError(message, cause),
      );
      context.writeArray("webhooks", [
        ...context.readArray<WebhookDelivery>("webhooks"),
        delivery,
      ]);
      return cloneEventsValue(delivery);
    },
    async listEvents(options) {
      return pageContractRecords(
        context.readArray<EventEnvelope>("events"),
        options,
        (message) => new EventsContractError(message),
      );
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed identity and session mock for tests, Storybook,
 * and offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, session TTL, and
 * environment guard settings used to isolate deterministic local state.
 * @returns An auth provider adapter implementing identity/session lifecycle and
 * health checks without importing an auth provider SDK.
 */
export function createMemoryAuthAdapter(
  options?: MemoryAuthAdapterOptions,
): AuthProviderAdapter {
  const context = createContractContext(
    "auth",
    options,
    (message, cause) => new AuthContractError(message, cause),
  );
  const identities = () => context.readArray<AuthIdentity>("identities");
  const sessions = () => context.readArray<AuthSessionContract>("sessions");
  const sessionTtlSeconds = options?.sessionTtlSeconds;
  const cloneAuthValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new AuthContractError(message, cause),
    );

  if (
    sessionTtlSeconds !== undefined &&
    (!Number.isInteger(sessionTtlSeconds) || sessionTtlSeconds <= 0)
  ) {
    throw new AuthContractError(
      "memory auth sessionTtlSeconds must be a positive integer",
    );
  }

  return {
    async getIdentity(identityId: string) {
      assertContractKey(
        identityId,
        (message) => new AuthContractError(message),
      );
      return (
        identities().find((identity) => identity.id === identityId) ?? null
      );
    },
    async createIdentity(identity: AuthIdentity) {
      assertContractKey(
        identity.id,
        (message) => new AuthContractError(message),
      );
      context.writeArray("identities", [
        ...identities().filter((candidate) => candidate.id !== identity.id),
        cloneAuthValue(identity),
      ]);
      return cloneAuthValue(identity);
    },
    async createSession(identityId: string) {
      assertContractKey(
        identityId,
        (message) => new AuthContractError(message),
      );
      const existingIdentity = identities().find(
        (identity) => identity.id === identityId,
      );
      if (!existingIdentity) {
        throw new AuthContractError(
          `memory auth createSession requires an existing identity for ${identityId}`,
        );
      }
      const session: AuthSessionContract = {
        id: context.idFactory(),
        identityId,
        token: context.idFactory(),
        expiresAt: sessionTtlSeconds
          ? new Date(
              new Date(context.now()).getTime() + sessionTtlSeconds * 1000,
            ).toISOString()
          : undefined,
        createdAt: context.now(),
      };
      context.writeArray("sessions", [...sessions(), session]);
      return cloneAuthValue(session);
    },
    async verifySession(token: string) {
      assertContractKey(token, (message) => new AuthContractError(message));
      const session = sessions().find((candidate) => candidate.token === token);
      if (!session) {
        return null;
      }
      if (
        session.expiresAt &&
        new Date(session.expiresAt).getTime() <=
          new Date(context.now()).getTime()
      ) {
        return null;
      }
      return cloneAuthValue(session);
    },
    async revokeSession(sessionId: string) {
      assertContractKey(sessionId, (message) => new AuthContractError(message));
      context.writeArray(
        "sessions",
        sessions().filter((session) => session.id !== sessionId),
      );
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed payments mock for tests, Storybook, and
 * offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, plan fixtures, and
 * environment guard settings used to isolate deterministic local state.
 * @returns A payments provider adapter implementing plans, checkout,
 * subscription, feature-gate, and health methods without importing Stripe or
 * another payment SDK.
 */
export function createMemoryPaymentsAdapter(
  options?: MemoryPaymentsAdapterOptions,
): PaymentsProviderAdapter {
  const context = createContractContext(
    "payments",
    options,
    (message, cause) => new PaymentsContractError(message, cause),
  );
  const clonePaymentsValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new PaymentsContractError(message, cause),
    );
  const plans = clonePaymentsValue(
    (
      options?.plans ??
      ([
        {
          id: "memory-free",
          name: "Memory Free",
          currency: "USD",
          amount: 0,
          interval: "month",
          features: ["core"],
        },
      ] satisfies readonly PaymentPlanContract[])
    ).map((plan) =>
      parseContractValue(
        PaymentPlanContractSchema,
        plan,
        "payment plan",
        (message, cause) => new PaymentsContractError(message, cause),
      ),
    ),
  );
  const checkouts = () =>
    context.readArray<PaymentCheckoutSession>("checkouts");
  const subscriptions = () =>
    context.readArray<PaymentSubscriptionContract>("subscriptions");
  const getSubscriptionForIdentity = async (
    identityId: string,
  ): Promise<PaymentSubscriptionContract | null> => {
    assertContractKey(
      identityId,
      (message) => new PaymentsContractError(message),
    );
    return (
      subscriptions()
        .filter((subscription) => subscription.identityId === identityId)
        .at(-1) ?? null
    );
  };

  return {
    async listPlans() {
      return clonePaymentsValue([...plans]);
    },
    async createCheckout(input) {
      const checkedInput = parseContractValue(
        PaymentCheckoutInputSchema,
        input,
        "checkout input",
        (message, cause) => new PaymentsContractError(message, cause),
      );
      if (!plans.some((plan) => plan.id === checkedInput.planId)) {
        throw new PaymentsContractError(
          `Unknown memory plan ${checkedInput.planId}`,
        );
      }
      const now = context.now();
      const checkout: PaymentCheckoutSession = {
        id: context.idFactory(),
        planId: checkedInput.planId,
        identityId: checkedInput.identityId,
        url: checkedInput.successUrl ?? "https://memory.geenius.local/checkout",
        status: "created",
        createdAt: now,
      };
      const subscription: PaymentSubscriptionContract = {
        id: `sub_${checkout.id}`,
        planId: checkedInput.planId,
        identityId: checkedInput.identityId,
        status: "active",
        currentPeriodStart: now,
        currentPeriodEnd: addDaysToIsoTimestamp(now, 30),
        cancelAtPeriodEnd: false,
        createdAt: now,
        updatedAt: now,
        metadata: {},
      };
      context.writeArray("checkouts", [...checkouts(), checkout]);
      context.writeArray("subscriptions", [
        ...subscriptions().filter(
          (candidate) => candidate.identityId !== input.identityId,
        ),
        subscription,
      ]);
      return clonePaymentsValue(checkout);
    },
    async getSubscription(identityId: string) {
      return getSubscriptionForIdentity(identityId);
    },
    async cancelSubscription(identityId: string) {
      assertContractKey(
        identityId,
        (message) => new PaymentsContractError(message),
      );
      const cancelledAt = context.now();
      context.writeArray(
        "subscriptions",
        subscriptions().map((subscription) =>
          subscription.identityId === identityId
            ? {
                ...subscription,
                status: "cancelled" as const,
                cancelAtPeriodEnd: false,
                cancelledAt,
                updatedAt: cancelledAt,
              }
            : subscription,
        ),
      );
    },
    async isFeatureEnabled(identityId: string, feature: string) {
      assertContractKey(
        identityId,
        (message) => new PaymentsContractError(message),
      );
      const subscription = await getSubscriptionForIdentity(identityId);
      if (
        subscription?.status !== "active" &&
        subscription?.status !== "trialing"
      ) {
        return false;
      }
      const plan = plans.find(
        (candidate) => candidate.id === subscription.planId,
      );
      return plan?.features.includes(feature) ?? false;
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates a localStorage-backed deployment-platform mock for tests, Storybook,
 * and offline prototypes.
 *
 * @param options Optional storage, namespace, id, clock, and environment guard
 * settings used to isolate deterministic local state.
 * @returns A deploy adapter implementing deploy, rollback, lookup, list, and
 * health methods without importing Cloudflare, Vercel, Netlify, or Heroku SDKs.
 */
export function createMemoryDeployAdapter(
  options?: MemoryContractAdapterOptions,
): DeployAdapter {
  const context = createContractContext(
    "deploy",
    options,
    (message, cause) => new DeployContractError(message, cause),
  );
  const deployments = () => context.readArray<DeploymentResult>("deployments");
  const cloneDeployValue = <T>(value: T): T =>
    cloneValue(
      value,
      (message, cause) => new DeployContractError(message, cause),
    );

  return {
    async deploy(target: DeploymentTarget) {
      const checkedTarget = parseContractValue(
        DeploymentTargetSchema,
        target,
        "deployment target",
        (message, cause) => new DeployContractError(message, cause),
      );
      const now = context.now();
      const deployment: DeploymentResult = {
        id: context.idFactory(),
        targetId: checkedTarget.id,
        status: "ready",
        url: `https://${checkedTarget.projectId}.${checkedTarget.provider}.memory.local`,
        createdAt: now,
        updatedAt: now,
      };
      context.writeArray("deployments", [...deployments(), deployment]);
      return cloneDeployValue(deployment);
    },
    async rollback(deploymentId: string) {
      assertContractKey(
        deploymentId,
        (message) => new DeployContractError(message),
      );
      const existing = deployments().find(
        (deployment) => deployment.id === deploymentId,
      );
      if (!existing) {
        throw new DeployContractError(
          `Unknown memory deployment ${deploymentId}`,
        );
      }
      const rolledBack: DeploymentResult = {
        ...existing,
        status: "rolled_back",
        updatedAt: context.now(),
      };
      context.writeArray(
        "deployments",
        deployments().map((deployment) =>
          deployment.id === deploymentId ? rolledBack : deployment,
        ),
      );
      return cloneDeployValue(rolledBack);
    },
    async getDeployment(deploymentId: string) {
      assertContractKey(
        deploymentId,
        (message) => new DeployContractError(message),
      );
      return (
        deployments().find((deployment) => deployment.id === deploymentId) ??
        null
      );
    },
    async listDeployments(targetId?: string) {
      const records = deployments();
      return cloneDeployValue(
        targetId
          ? records.filter((deployment) => deployment.targetId === targetId)
          : records,
      );
    },
    async getHealth() {
      return { ok: true, checkedAt: context.now() };
    },
  };
}

/**
 * Creates the aggregate local/dev adapter bundle for every memory contract
 * domain.
 *
 * @param options Optional shared storage, namespace, id, clock, auth session,
 * payment plan, and environment guard settings passed to each domain factory.
 * @returns A bundled set of storage, cache, queue, events, auth, payments, and
 * deploy memory adapters for tests, Storybook, and offline prototypes.
 */
export function createMemoryAdapters(options?: MemoryAdaptersOptions) {
  return {
    storage: createMemoryStorageAdapter(options),
    cache: createMemoryCacheAdapter(options),
    queue: createMemoryQueueAdapter(options),
    events: createMemoryEventsAdapter(options),
    auth: createMemoryAuthAdapter(options),
    payments: createMemoryPaymentsAdapter(options),
    deploy: createMemoryDeployAdapter(options),
  };
}

/** @doc SDK-free domain contract interfaces exposed consistently across launch DB backend subpaths. */
export type {
  AuthProviderAdapter,
  CacheAdapter,
  DeployAdapter,
  EventsAdapter,
  PaymentsProviderAdapter,
  QueueAdapter,
  StorageAdapter,
} from "@geenius/adapters";
/** @doc SDK-free domain Zod schemas and error classes exposed by this backend contract subpath. */
export {
  AuthContractError,
  AuthIdentitySchema,
  AuthSessionContractSchema,
  CacheContractError,
  CacheEntrySchema,
  DeployContractError,
  DeploymentResultSchema,
  DeploymentTargetSchema,
  EventEnvelopeSchema,
  EventsContractError,
  PaymentCheckoutInputSchema,
  PaymentPlanContractSchema,
  PaymentSubscriptionContractSchema,
  PaymentsContractError,
  QueueContractError,
  QueueJobSchema,
  StorageContractError,
  StorageObjectSchema,
} from "@geenius/adapters";
